import {
  any,
  identity,
  isEmpty,
  lensPath,
  memoizeWith,
  mergeDeepRight,
  pathEq,
  set,
  uniq,
} from 'ramda';

export interface Permissions {
  [key: string]: Permissions | '*' | 'a' | 'r';
}

const getPossiblePermissionPaths = memoizeWith(
  (sections: string[]) => sections.join(':'),
  (sections: string[]): string[][] => {
    const [first, ...rest] = sections;

    if (isEmpty(rest)) return first === 'a' ? [['a']] : first === 'r' ? [['r'], ['a']] : [[first]];

    const restOptions = getPossiblePermissionPaths(rest);
    const strictOptions = restOptions.map((r) => [first, ...r]);
    const wildcardOptions = restOptions.map(([_, ...r]) => [first, '*', ...r]);
    return uniq([...strictOptions, ...wildcardOptions, [first, '*']]);
  }
);

const getPermissionAsObject = memoizeWith(
  identity,
  (permission: string): Permissions => set(lensPath(permission.split(':')), true, {})
);

export const transformPermissions = (permissions: string[]): Permissions =>
  (permissions || []).map(getPermissionAsObject).reduce((acc, v) => mergeDeepRight(acc, v), {});

export const checkPermission = (
  requiredPermission: string,
  userPermissions?: Permissions
): boolean => {
  const userHasPermissions = !!userPermissions && !isEmpty(userPermissions);
  const permissionToCheckNotEmpty =
    !!requiredPermission && (requiredPermission?.trim().length || 0) > 0;
  const rawSections = requiredPermission?.split(':');
  const sections = rawSections?.filter((s) => !!s) || [];

  if (
    userHasPermissions &&
    permissionToCheckNotEmpty &&
    sections.length > 1 &&
    rawSections.length === sections.length
  ) {
    const paths = getPossiblePermissionPaths(sections);
    return (
      pathEq(['*', '*'], true, userPermissions || []) ||
      any((p) => pathEq(p, true, userPermissions), paths)
    );
  }

  return false;
};

export const checkPermissions = (
  permissions: string[] | undefined,
  userPermissions: Permissions | undefined
): boolean =>
  pathEq(['*', '*'], true, userPermissions || []) ||
  any((p) => checkPermission(p, userPermissions), permissions || []);
